import { useState, useEffect, useCallback } from "react";
import {
  CreditCard, Plus, Edit, Trash2, CheckCircle, AlertCircle,
  X, Users, TrendingUp, PlusCircle, MinusCircle, Key, Eye, EyeOff,
  Settings, Zap
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// ── Pakket Modal ─────────────────────────────────────────────────────────────
function PakketModal({ pakket, onClose, onSave, token }) {
  const isEdit = Boolean(pakket?.id);
  const [form, setForm] = useState({
    naam: pakket?.naam || "",
    credits: pakket?.credits || 10,
    prijs: pakket?.prijs || 25,
    beschrijving: pakket?.beschrijving || "",
    actief: pakket?.actief ?? true,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const inputCls = "w-full bg-[#1a1a1a] border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-[#C9A84C]/50 placeholder-zinc-600";
  const labelCls = "text-zinc-400 text-xs font-semibold uppercase tracking-wide mb-1.5 block";

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.naam || form.credits <= 0) { setError("Naam en credits zijn verplicht"); return; }
    setSaving(true); setError("");
    try {
      const url = isEdit ? `${API}/api/schedule/creditpakketten/${pakket.id}` : `${API}/api/schedule/creditpakketten`;
      const r = await fetch(url, {
        method: isEdit ? "PUT" : "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...form, credits: Number(form.credits), prijs: Number(form.prijs) }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Opslaan mislukt");
      onSave();
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-[#0f0f0f] border border-white/10 rounded-2xl w-full max-w-sm">
        <div className="flex items-center justify-between p-5 border-b border-white/8">
          <h3 className="text-white font-bold">{isEdit ? "Pakket bewerken" : "Nieuw creditpakket"}</h3>
          <button onClick={onClose} className="text-zinc-500 hover:text-white"><X size={18} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label className={labelCls}>Naam *</label>
            <input value={form.naam} onChange={e => set("naam", e.target.value)} placeholder="bv. Starter 10 credits" className={inputCls} data-testid="pakket-naam-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Credits *</label>
              <input type="number" min={1} value={form.credits} onChange={e => set("credits", e.target.value)} className={inputCls} data-testid="pakket-credits-input" />
            </div>
            <div>
              <label className={labelCls}>Prijs (€)</label>
              <input type="number" min={0} step={0.01} value={form.prijs} onChange={e => set("prijs", e.target.value)} className={inputCls} data-testid="pakket-prijs-input" />
            </div>
          </div>
          <div>
            <label className={labelCls}>Beschrijving</label>
            <input value={form.beschrijving} onChange={e => set("beschrijving", e.target.value)} placeholder="Optionele toelichting" className={inputCls} />
          </div>
          <div className="flex items-center gap-3">
            <button type="button" onClick={() => set("actief", !form.actief)}
              className={`w-10 h-6 rounded-full transition-colors relative ${form.actief ? "bg-[#C9A84C]" : "bg-zinc-700"}`}>
              <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${form.actief ? "right-1" : "left-1"}`} />
            </button>
            <span className="text-zinc-400 text-sm">{form.actief ? "Actief" : "Inactief"}</span>
          </div>
          {error && <p className="text-red-400 text-sm">{error}</p>}
          <div className="flex gap-3">
            <button type="button" onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-white/10 text-zinc-400 text-sm font-semibold">Annuleren</button>
            <button type="submit" disabled={saving} data-testid="save-pakket-btn"
              className="flex-1 py-2.5 rounded-xl bg-[#C9A84C] text-black text-sm font-bold disabled:opacity-40">
              {saving ? "Opslaan..." : isEdit ? "Bijwerken" : "Aanmaken"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Credits Aanpassen Modal ───────────────────────────────────────────────────
function CreditsModal({ gebruiker, onClose, onSave, token }) {
  const [credits, setCredits] = useState(0);
  const [omschrijving, setOmschrijving] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const inputCls = "w-full bg-[#1a1a1a] border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-[#C9A84C]/50 placeholder-zinc-600";

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!omschrijving.trim()) { setError("Omschrijving is verplicht"); return; }
    setSaving(true); setError("");
    try {
      const r = await fetch(`${API}/api/schedule/credits/handmatig`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ sporter_id: gebruiker.id, credits: Number(credits), omschrijving }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Mislukt");
      onSave(d.nieuw_saldo);
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-[#0f0f0f] border border-white/10 rounded-2xl w-full max-w-sm">
        <div className="flex items-center justify-between p-5 border-b border-white/8">
          <div>
            <h3 className="text-white font-bold">Credits aanpassen</h3>
            <p className="text-zinc-500 text-xs mt-0.5">{gebruiker.name} · huidig: {gebruiker.saldo} cr.</p>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white"><X size={18} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label className="text-zinc-400 text-xs font-semibold uppercase tracking-wide mb-1.5 block">Credits (negatief = afschrijven)</label>
            <input type="number" value={credits} onChange={e => setCredits(e.target.value)} className={inputCls} data-testid="handmatig-credits-input" />
            <p className="text-zinc-600 text-xs mt-1">Nieuw saldo: {gebruiker.saldo + Number(credits)} credits</p>
          </div>
          <div>
            <label className="text-zinc-400 text-xs font-semibold uppercase tracking-wide mb-1.5 block">Omschrijving *</label>
            <input value={omschrijving} onChange={e => setOmschrijving(e.target.value)} placeholder="bv. Welkomstbonus, correctie..." className={inputCls} />
          </div>
          {error && <p className="text-red-400 text-sm">{error}</p>}
          <div className="flex gap-3">
            <button type="button" onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-white/10 text-zinc-400 text-sm font-semibold">Annuleren</button>
            <button type="submit" disabled={saving} data-testid="save-handmatig-credits-btn"
              className="flex-1 py-2.5 rounded-xl bg-[#C9A84C] text-black text-sm font-bold disabled:opacity-40">
              {saving ? "..." : "Opslaan"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function AdminCreditsTab({ token }) {
  const [info, setInfo] = useState(null);
  const [pakketten, setPakketten] = useState([]);
  const [gebruikers, setGebruikers] = useState([]);
  const [transacties, setTransacties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState("pakketten");
  const [showPakketModal, setShowPakketModal] = useState(false);
  const [editPakket, setEditPakket] = useState(null);
  const [creditsModal, setCreditsModal] = useState(null);
  const [mollieKey, setMollieKey] = useState("");
  const [showMollieKey, setShowMollieKey] = useState(false);
  const [savingMollie, setSavingMollie] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const h = { Authorization: `Bearer ${token}` };
      const [r1, r2, r3, r4] = await Promise.all([
        fetch(`${API}/api/admin/credits/info`, { headers: h }),
        fetch(`${API}/api/schedule/creditpakketten`, { headers: h }),
        fetch(`${API}/api/admin/credits/gebruikers`, { headers: h }),
        fetch(`${API}/api/admin/credits/transacties`, { headers: h }),
      ]);
      if (r1.ok) setInfo(await r1.json());
      if (r2.ok) setPakketten((await r2.json()).pakketten || []);
      if (r3.ok) setGebruikers((await r3.json()).gebruikers || []);
      if (r4.ok) {
        const d = await r4.json();
        setTransacties([...(d.transacties || []), ...(d.orders || [])].sort(
          (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)
        ));
      }
    } catch {}
    setLoading(false);
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const handleDeletePakket = async (id) => {
    if (!window.confirm("Pakket verwijderen?")) return;
    try {
      const r = await fetch(`${API}/api/schedule/creditpakketten/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!r.ok) throw new Error((await r.json()).detail);
      showToast("Pakket verwijderd");
      load();
    } catch (e) { showToast(e.message, "error"); }
  };

  const handleSaveMollie = async (e) => {
    e.preventDefault();
    setSavingMollie(true);
    try {
      const r = await fetch(`${API}/api/admin/credits/mollie-key`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ mollie_api_key: mollieKey.trim() }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Opslaan mislukt");
      showToast(d.geconfigureerd ? "Mollie sleutel opgeslagen" : "Mollie sleutel verwijderd");
      setMollieKey("");
      load();
    } catch (e) { showToast(e.message, "error"); }
    finally { setSavingMollie(false); }
  };

  const totalSaldo = gebruikers.reduce((s, g) => s + (g.saldo || 0), 0);
  const views = [
    { key: "pakketten", label: "Pakketten" },
    { key: "gebruikers", label: `Gebruikers (${gebruikers.length})` },
    { key: "geschiedenis", label: `Geschiedenis (${transacties.length})` },
    { key: "instellingen", label: "Mollie" },
  ];

  return (
    <div className="max-w-3xl" data-testid="admin-credits-tab">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="text-[#C9A84C] text-xs font-bold uppercase tracking-widest mb-1">FitSmith Eigen Trainingen</p>
          <h2 className="text-xl font-bold text-white">Credits & Pakketten</h2>
          <p className="text-zinc-500 text-sm mt-1">Verkoop credits aan sporters voor je eigen roosterlessen</p>
        </div>
        {info && (
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-xl text-xs font-bold ${info.mollie_geconfigureerd ? "bg-green-500/10 text-green-400" : "bg-yellow-500/10 text-yellow-400"}`}>
            {info.mollie_geconfigureerd ? <CheckCircle size={12} /> : <AlertCircle size={12} />}
            Mollie {info.mollie_geconfigureerd ? "actief" : "niet ingesteld"}
          </div>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        {[
          { label: "Pakketten", val: pakketten.length, icon: <CreditCard size={16} /> },
          { label: "Gebruikers", val: gebruikers.length, icon: <Users size={16} /> },
          { label: "Totaal saldo", val: `${totalSaldo} cr.`, icon: <TrendingUp size={16} /> },
        ].map(s => (
          <div key={s.label} className="bg-[#111] border border-white/8 rounded-2xl p-4 text-center">
            <div className="text-[#C9A84C] flex justify-center mb-2">{s.icon}</div>
            <p className="text-white font-black text-xl">{s.val}</p>
            <p className="text-zinc-600 text-xs mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Sub-navigatie */}
      <div className="flex gap-1 bg-[#111] border border-white/5 rounded-xl p-1 mb-6">
        {views.map(v => (
          <button key={v.key} onClick={() => setActiveView(v.key)}
            className={`flex-1 py-1.5 rounded-lg text-xs font-bold transition-all ${activeView === v.key ? "text-black" : "text-zinc-400 hover:text-white"}`}
            style={activeView === v.key ? { background: "#C9A84C" } : {}}>
            {v.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-16 bg-[#111] rounded-xl animate-pulse" />)}</div>
      ) : activeView === "pakketten" ? (
        /* ── PAKKETTEN ── */
        <div>
          <div className="flex justify-end mb-4">
            <button onClick={() => { setEditPakket(null); setShowPakketModal(true); }}
              data-testid="nieuw-pakket-btn"
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-black text-sm font-bold"
              style={{ background: "#C9A84C" }}>
              <Plus size={14} /> Nieuw pakket
            </button>
          </div>

          {!info?.mollie_geconfigureerd && (
            <div className="flex items-start gap-3 bg-yellow-500/8 border border-yellow-500/20 rounded-xl p-4 mb-4">
              <AlertCircle size={16} className="text-yellow-400 shrink-0 mt-0.5" />
              <div>
                <p className="text-yellow-300 text-sm font-semibold">Mollie niet geconfigureerd</p>
                <p className="text-yellow-500/70 text-xs mt-0.5">Voer je Mollie API-sleutel in via het tabblad "Mollie" zodat sporters kunnen betalen.</p>
              </div>
            </div>
          )}

          {pakketten.length === 0 ? (
            <div className="bg-[#111] border border-white/8 rounded-2xl p-10 text-center">
              <CreditCard size={28} className="text-zinc-700 mx-auto mb-3" />
              <p className="text-zinc-400 font-semibold mb-1">Nog geen creditpakketten</p>
              <p className="text-zinc-600 text-sm mb-4">Maak een pakket aan dat sporters kunnen kopen om je lessen te boeken.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {pakketten.map(p => (
                <div key={p.id} className="bg-[#111] border border-white/8 rounded-xl p-4 flex items-center justify-between gap-4"
                  data-testid={`pakket-row-${p.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-white font-bold text-sm">{p.naam}</p>
                      {!p.actief && <span className="text-[10px] px-2 py-0.5 rounded-full bg-zinc-700 text-zinc-400 font-semibold">Inactief</span>}
                    </div>
                    <p className="text-zinc-500 text-xs mt-0.5">
                      {p.credits} credits · €{Number(p.prijs).toFixed(2).replace(".", ",")}
                      {p.beschrijving && ` · ${p.beschrijving}`}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <p className="text-[#C9A84C] font-black text-sm">€{Number(p.prijs).toFixed(2).replace(".", ",")}</p>
                    <button onClick={() => { setEditPakket(p); setShowPakketModal(true); }}
                      data-testid={`edit-pakket-btn-${p.id}`}
                      className="p-1.5 rounded-lg text-zinc-500 hover:text-[#C9A84C] hover:bg-[#C9A84C]/10 transition-all">
                      <Edit size={13} />
                    </button>
                    <button onClick={() => handleDeletePakket(p.id)}
                      data-testid={`delete-pakket-btn-${p.id}`}
                      className="p-1.5 rounded-lg text-zinc-600 hover:text-red-400 hover:bg-red-500/10 transition-all">
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : activeView === "gebruikers" ? (
        /* ── GEBRUIKERS ── */
        <div>
          <p className="text-zinc-500 text-sm mb-4">
            Cursisten met toegang tot het persoonlijk rooster en hun huidige credit saldo.
          </p>
          {gebruikers.length === 0 ? (
            <div className="bg-[#111] border border-white/8 rounded-2xl p-10 text-center">
              <Users size={28} className="text-zinc-700 mx-auto mb-3" />
              <p className="text-zinc-400 font-semibold mb-1">Geen gebruikers met roostertoegang</p>
              <p className="text-zinc-600 text-sm">Schakel roostertoegang in via de Cursisten tab voor individuele gebruikers.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {gebruikers.map(g => (
                <div key={g.id} className="bg-[#111] border border-white/8 rounded-xl p-3.5 flex items-center justify-between gap-3"
                  data-testid={`gebruiker-row-${g.id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-semibold truncate">{g.name}</p>
                    <p className="text-zinc-600 text-xs truncate">{g.email}</p>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <div className="text-right">
                      <p className="text-[#C9A84C] font-black text-sm tabular-nums">{g.saldo}</p>
                      <p className="text-zinc-600 text-[10px]">credits</p>
                    </div>
                    <button onClick={() => setCreditsModal(g)}
                      data-testid={`aanpassen-credits-btn-${g.id}`}
                      className="p-2 rounded-lg border border-white/8 text-zinc-400 hover:text-[#C9A84C] hover:border-[#C9A84C]/30 transition-all">
                      <Settings size={13} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : activeView === "geschiedenis" ? (
        /* ── GESCHIEDENIS ── */
        <div data-testid="admin-credits-geschiedenis">
          {transacties.length === 0 ? (
            <div className="bg-[#111] border border-white/8 rounded-2xl p-10 text-center">
              <p className="text-zinc-400 font-semibold">Nog geen transacties</p>
            </div>
          ) : (
            <div className="space-y-2">
              {transacties.map((t, i) => {
                const bedrag = t.credits || t.pakket_credits || 0;
                const naam = t.sporter_naam || t.sporter_email || t.sporter_id || "—";
                const datum = t.created_at ? new Date(t.created_at).toLocaleString("nl-NL", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";
                const isOrder = Boolean(t.order_id);
                return (
                  <div key={i} className="bg-[#111] border border-white/8 rounded-xl p-3.5 flex items-center justify-between gap-3"
                    data-testid={`admin-transactie-row-${i}`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${bedrag > 0 ? "bg-green-500/10" : "bg-red-500/10"}`}>
                        <span className={`text-xs font-black ${bedrag > 0 ? "text-green-400" : "text-red-400"}`}>{bedrag > 0 ? "+" : ""}{bedrag}</span>
                      </div>
                      <div>
                        <p className="text-white text-sm font-semibold">{naam}</p>
                        <p className="text-zinc-600 text-xs">{t.omschrijving || t.type || (isOrder ? `Pakket aankoop · ${t.status || ""}` : "Transactie")}</p>
                      </div>
                    </div>
                    <p className="text-zinc-500 text-xs shrink-0">{datum}</p>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ) : (
        /* ── MOLLIE INSTELLINGEN ── */
        <div className="max-w-md">
          <div className="bg-[#111] border border-white/8 rounded-2xl p-5">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-[#C9A84C]/10 flex items-center justify-center">
                <Key size={18} className="text-[#C9A84C]" />
              </div>
              <div>
                <h3 className="text-white font-bold">Mollie API-sleutel</h3>
                <p className="text-zinc-500 text-xs mt-0.5">
                  {info?.mollie_geconfigureerd
                    ? `Actief · eindigt op ${info.mollie_key_preview}`
                    : "Nog niet geconfigureerd"}
                </p>
              </div>
            </div>

            {info?.mollie_geconfigureerd && (
              <div className="flex items-start gap-2 bg-green-500/8 border border-green-500/20 rounded-xl p-3 mb-4">
                <CheckCircle size={14} className="text-green-400 shrink-0 mt-0.5" />
                <p className="text-green-300 text-sm">Mollie is actief. Sporters kunnen creditpakketten kopen via iDEAL.</p>
              </div>
            )}

            <form onSubmit={handleSaveMollie} className="space-y-4">
              <div>
                <label className="text-zinc-400 text-xs font-semibold uppercase tracking-wide mb-1.5 block">
                  {info?.mollie_geconfigureerd ? "Nieuwe sleutel invoeren (om te wijzigen)" : "API-sleutel"}
                </label>
                <div className="relative">
                  <input
                    type={showMollieKey ? "text" : "password"}
                    value={mollieKey}
                    onChange={e => setMollieKey(e.target.value)}
                    placeholder={info?.mollie_geconfigureerd ? "Laat leeg om huidig te behouden" : "live_... of test_..."}
                    data-testid="mollie-key-input"
                    className="w-full bg-[#1a1a1a] border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm pr-10 focus:outline-none focus:border-[#C9A84C]/50 placeholder-zinc-600"
                  />
                  <button type="button" onClick={() => setShowMollieKey(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white">
                    {showMollieKey ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>
                <p className="text-zinc-600 text-xs mt-1">Gebruik <code className="text-zinc-400">test_...</code> voor test of <code className="text-zinc-400">live_...</code> voor productie.</p>
              </div>
              <div className="flex gap-3">
                {info?.mollie_geconfigureerd && (
                  <button type="button"
                    onClick={() => handleSaveMollie({ preventDefault: () => {}, target: { mollieKey: "" } })}
                    disabled={savingMollie}
                    data-testid="verwijder-mollie-key-btn"
                    className="flex-1 py-2.5 rounded-xl border border-red-500/30 text-red-400 text-sm font-semibold hover:bg-red-500/10 transition-colors disabled:opacity-40">
                    Verwijderen
                  </button>
                )}
                <button type="submit" disabled={savingMollie || !mollieKey.trim()}
                  data-testid="save-mollie-key-btn"
                  className="flex-1 py-2.5 rounded-xl bg-[#C9A84C] text-black text-sm font-bold disabled:opacity-40">
                  {savingMollie ? "Opslaan..." : "Sleutel opslaan"}
                </button>
              </div>
            </form>
          </div>

          <div className="bg-[#111] border border-white/8 rounded-2xl p-5 mt-4">
            <div className="flex items-center gap-2 mb-3">
              <Zap size={14} className="text-[#C9A84C]" />
              <p className="text-zinc-400 text-sm font-semibold">Hoe werkt het?</p>
            </div>
            <ol className="space-y-2 text-zinc-500 text-sm">
              <li className="flex gap-2"><span className="text-[#C9A84C] font-bold shrink-0">1.</span> Maak creditpakketten aan (bv. "10 credits voor €25")</li>
              <li className="flex gap-2"><span className="text-[#C9A84C] font-bold shrink-0">2.</span> Voer je Mollie API-sleutel in zodat betalingen worden verwerkt</li>
              <li className="flex gap-2"><span className="text-[#C9A84C] font-bold shrink-0">3.</span> Schakel roostertoegang in voor cursisten via de Cursisten tab</li>
              <li className="flex gap-2"><span className="text-[#C9A84C] font-bold shrink-0">4.</span> Cursisten zien pakketten op hun account en kunnen credits kopen via iDEAL</li>
              <li className="flex gap-2"><span className="text-[#C9A84C] font-bold shrink-0">5.</span> Betalingen komen direct op je Mollie rekening binnen</li>
            </ol>
          </div>
        </div>
      )}

      {/* Modals */}
      {showPakketModal && (
        <PakketModal
          pakket={editPakket}
          token={token}
          onClose={() => { setShowPakketModal(false); setEditPakket(null); }}
          onSave={() => { setShowPakketModal(false); setEditPakket(null); load(); showToast(editPakket ? "Pakket bijgewerkt" : "Pakket aangemaakt"); }}
        />
      )}
      {creditsModal && (
        <CreditsModal
          gebruiker={creditsModal}
          token={token}
          onClose={() => setCreditsModal(null)}
          onSave={(nieuwSaldo) => {
            setGebruikers(prev => prev.map(g => g.id === creditsModal.id ? { ...g, saldo: nieuwSaldo } : g));
            setCreditsModal(null);
            showToast("Credits aangepast");
          }}
        />
      )}

      {/* Toast */}
      {toast && (
        <div className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold shadow-2xl ${toast.type === "success" ? "bg-[#111] border border-[#C9A84C]/40 text-white" : "bg-red-950 border border-red-500/40 text-red-300"}`}>
          {toast.type === "success" ? <CheckCircle size={16} className="text-[#C9A84C]" /> : <AlertCircle size={16} className="text-red-400" />}
          {toast.msg}
        </div>
      )}
    </div>
  );
}
